import { useCallback, useMemo, useState } from "react";
import classNames from "classnames";
import { OrganizationHistoryQuery } from "@common/types/auditLog";
import { OrganizationActivityViewSchema } from "@common/types/views/organizationActivityView";
import { View } from "@common/types/views/views";
import { CalendarIcon } from "@icons";
import { useActiveOrganization } from "@hooks/useActiveOrganization";
import { useOrganizationActivityViewStore } from "@hooks/view/useOrganizationActivityViewStore";
import { usePinnedDatePresets } from "@hooks/usePinnedDatePresets";
import { ORGANIZATION_USER_CACHE_KEYS } from "@hooks/useOrganizationUserCache";
import { useOrganizationHistory } from "@api/organizationAuditLog.api";
import { DateRangeFilterNext } from "@design-system/DateRangeFilter/DateRangeFilterNext";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { SINCE_LAST_VISIT_EMPTY_STATE } from "@components/activityTimeline/activityTimelineRange.lib";
import { ActivityChartDensityButton } from "@components/activityTimeline/ActivityChartDensityButton";
import { PaginatedTableFooter } from "@components/organizationView/shared/PaginatedTableFooter";
import { useClampedPageCount } from "@components/organizationView/shared/useClampedPageCount";
import { SUGGESTED_PRESETS } from "@helpers/date";
import { ViewTableMeta } from "@hooks/view/useViewTableMeta";
import { useTypedFlags } from "@hooks/useTypedFlags";
import { Loader } from "@design-system/Loader/Loader";
import { OrganizationClientFilterCombobox } from "./components/OrganizationClientFilterCombobox";
import { OrganizationActivityToolbar } from "./OrganizationActivityToolbar";
import { OrganizationActivityTableCallbacks } from "./cells/OrganizationActionsCell";
import { OrganizationActivityDetailsDrawer } from "./details/OrganizationActivityDetailsDrawer";
import { OrganizationActivityPeopleProvider } from "./OrganizationActivityPeopleProvider";
import {
    OrganizationActivityRow,
    toOrganizationActivityRow,
} from "./organizationActivityRow";
import { organizationActivityViewConfig } from "./organizationActivityViewConfig";
import { OrganizationActivityTimeline } from "./timeline/OrganizationActivityTimeline";
import { useOrganizationActivityFilters } from "./useOrganizationActivityFilters";
import { useOrganizationClients } from "./useOrganizationClients";
import styles from "./OrganizationActivityPage.module.scss";

const PAGE_SIZE = 50;

const OrganizationActivityPageContent = () => {
    const organization = useActiveOrganization();
    const organizationId = organization?.id ?? "";
    const { view, setView } = useOrganizationActivityViewStore();
    const {
        page,
        setPage,
        filters,
        selection,
        pickableSeries,
        pickableOutcomes,
        isChannelFilterEnabled,
        dateRange,
        listFilter,
        timeline,
        isActivityTimelineEnabled,
        hasActiveFilters,
        hasPickedDates,
        isSinceLastVisitWindow,
        previewWindow,
        setPreviewWindow,
        chartDensity,
        historyStartClientName,
        handleDateRangeChange,
        handleSelectionChange,
        handleClientWorkspaceIdsChange,
        handleClearFilters,
    } = useOrganizationActivityFilters();
    const [selectedRow, setSelectedRow] =
        useState<OrganizationActivityRow | null>(null);
    const [isDetailsOpen, setIsDetailsOpen] = useState(false);

    const handleViewDetails = useCallback((row?: OrganizationActivityRow) => {
        if (!row) {
            return;
        }
        setSelectedRow(row);
        setIsDetailsOpen(true);
    }, []);

    const handleCloseDetails = useCallback(() => {
        setIsDetailsOpen(false);
    }, []);

    const tableMeta = useMemo<
        ViewTableMeta<OrganizationActivityTableCallbacks, never>
    >(
        () => ({
            callbacks: {
                onViewDetails: handleViewDetails,
            },
        }),
        [handleViewDetails],
    );

    const { pinnedPresets, onTogglePin } = usePinnedDatePresets({
        cacheKey:
            ORGANIZATION_USER_CACHE_KEYS.PINNED_DATE_PRESETS_HISTORY_CACHE_KEY,
        defaults: SUGGESTED_PRESETS.accounting,
    });

    const query: OrganizationHistoryQuery = useMemo(
        () => ({
            filters: listFilter,
            page,
            limit: PAGE_SIZE,
        }),
        [listFilter, page],
    );
    const { data, isLoading } = useOrganizationHistory({
        organizationId,
        query,
    });
    const rows = useMemo(
        () => data?.data.map(toOrganizationActivityRow) ?? [],
        [data],
    );

    const total = data?.total ?? 0;
    const pageCount = useClampedPageCount({
        totalPages: data?.totalPages,
        page,
        setPage,
    });

    const handleViewChange = useCallback(
        (nextView: View) => {
            const parsed = OrganizationActivityViewSchema.safeParse(nextView);
            if (parsed.success) {
                setView(parsed.data);
            }
        },
        [setView],
    );

    const emptyView = useCallback(() => {
        if (hasActiveFilters) {
            return (
                <EmptyState
                    header="No activity available"
                    ctaText="Clear filters"
                    action={handleClearFilters}
                />
            );
        }
        if (isSinceLastVisitWindow) {
            return (
                <EmptyState
                    {...SINCE_LAST_VISIT_EMPTY_STATE}
                    ctaText={undefined}
                />
            );
        }
        if (hasPickedDates) {
            return (
                <EmptyState
                    header="No activity available"
                    ctaText={undefined}
                />
            );
        }
        return (
            <EmptyState
                header="No activity yet"
                body="Changes to your firm's team and client tasks will show up here."
                ctaText={undefined}
            />
        );
    }, [
        hasActiveFilters,
        isSinceLastVisitWindow,
        hasPickedDates,
        handleClearFilters,
    ]);

    const footer = useMemo(
        () =>
            total > 0 ? (
                <PaginatedTableFooter
                    total={total}
                    pageCount={pageCount}
                    page={page}
                    onPageChange={setPage}
                    itemLabel="event"
                />
            ) : undefined,
        [total, pageCount, page, setPage],
    );

    return (
        <OrganizationActivityPeopleProvider>
            <main className={styles.container}>
                <OrganizationActivityToolbar
                    search={filters.search}
                    selection={selection}
                    pickableSeries={pickableSeries}
                    pickableOutcomes={pickableOutcomes}
                    clientWorkspaceIds={filters.clientWorkspaceIds}
                    isChannelFilterEnabled={isChannelFilterEnabled}
                    onSelectionChange={handleSelectionChange}
                    onClearFilters={
                        hasActiveFilters ? handleClearFilters : undefined
                    }
                >
                    {isActivityTimelineEnabled && (
                        <ActivityChartDensityButton
                            density={chartDensity.density}
                            hint={chartDensity.hint}
                            onDensityChange={chartDensity.setDensity}
                        />
                    )}
                    <DateRangeFilterNext
                        dateRange={dateRange}
                        onChange={handleDateRangeChange}
                        pinnedPresets={pinnedPresets}
                        onTogglePin={onTogglePin}
                        previewRange={previewWindow}
                        leftIcon={<CalendarIcon />}
                        {...(isActivityTimelineEnabled &&
                            timeline.dateFilterProps)}
                    />
                    <OrganizationClientFilterCombobox
                        clientWorkspaceIds={filters.clientWorkspaceIds}
                        onChange={handleClientWorkspaceIdsChange}
                    />
                </OrganizationActivityToolbar>

                {isActivityTimelineEnabled && (
                    <OrganizationActivityTimeline
                        organizationId={organizationId}
                        filter={listFilter}
                        range={timeline.range}
                        previewWindow={previewWindow}
                        today={timeline.today}
                        chartDensity={chartDensity}
                        onWindowChange={handleDateRangeChange}
                        onWindowPreview={setPreviewWindow}
                        onFiltersChange={handleSelectionChange}
                        historyStartClientName={historyStartClientName}
                    />
                )}

                <ViewTable
                    data={isLoading && !data ? null : rows}
                    view={view}
                    config={organizationActivityViewConfig}
                    className={classNames(styles.activityView, {
                        [styles.timelineTable]: isActivityTimelineEnabled,
                    })}
                    tableMeta={tableMeta}
                    onRowClick={handleViewDetails}
                    onViewChange={handleViewChange}
                    enableColumnResizing
                    fixedTopOffsetPx
                    emptyView={emptyView}
                    footer={footer}
                />

                <OrganizationActivityDetailsDrawer
                    row={selectedRow}
                    outcome={selection.outcome}
                    open={isDetailsOpen}
                    onClose={handleCloseDetails}
                />
            </main>
        </OrganizationActivityPeopleProvider>
    );
};

/**
 * With the timeline on, waits for the firm's clients so the first chart and
 * list already fit where its history starts instead of refetching once they
 * load. If they fail to load, the history starts at the default range.
 */
export const OrganizationActivityPage = () => {
    const { activityTimeline: isActivityTimelineEnabled } = useTypedFlags();
    const { isLoading } = useOrganizationClients();

    if (isActivityTimelineEnabled && isLoading) {
        return <Loader />;
    }
    return <OrganizationActivityPageContent />;
};
